import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Settings, BarChart2 } from 'lucide-react';
import { getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';

/**
 * Composant pour afficher la distribution des paliers de CPC des termes de recherche
 * avec conversions/coûts/COS - basé sur CpcTierBreakdown
 */
const SearchTermsCpcTierBreakdown = ({
  autoCharts,
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  performanceMaxData,
  campaignFilter = 'all',
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  onTierClick,
  selectedCpcTierFilter = null,
  userRole = 'USER'
}) => {
  const [tierData, setTierData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'totalCost', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Configuration des métriques pour ce composant
  const componentMetrics = useComponentMetrics('search_terms_cpc_tiers', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Fonction pour calculer les paliers CPC côté client pour les termes de recherche (optimisée pour gros datasets)
  const calculateSearchTermsCpcTiers = (searchTerms) => {
    // Calcul des paliers pour les termes de recherche
    
    if (searchTerms.length === 0) {
      return {
        tiers: [],
        totalTerms: 0,
        termsWithCpc: 0,
        cpcRange: { min: 0, max: 0 }
      };
    }
    
    // Optimisation pour gros datasets: traitement par chunks pour éviter stack overflow
    const CHUNK_SIZE = 10000;
    let minCpc = Infinity;
    let maxCpc = -Infinity;
    let termsWithCpcCount = 0;
    
    // Première passe: trouver min/max CPC par chunks
    for (let chunkStart = 0; chunkStart < searchTerms.length; chunkStart += CHUNK_SIZE) {
      const chunkEnd = Math.min(chunkStart + CHUNK_SIZE, searchTerms.length);
      const chunk = searchTerms.slice(chunkStart, chunkEnd);
      
      for (let i = 0; i < chunk.length; i++) {
        const term = chunk[i];
        const clicks = Number(term.clicks) || 0;
        const cost = Number(term.cost) || 0;
        const cpc = clicks > 0 ? (cost / clicks) : 0;
        
        if (cpc > 0) {
          termsWithCpcCount++;
          if (cpc < minCpc) minCpc = cpc;
          if (cpc > maxCpc) maxCpc = cpc;
        }
      }
      
    }
    
    
    if (termsWithCpcCount === 0) {
      return {
        tiers: [],
        totalTerms: searchTerms.length,
        termsWithCpc: 0,
        cpcRange: { min: 0, max: 0 }
      };
    }
    
    
    // Créer 8 tranches uniformes basées sur les plages de CPC égales
    const tierCount = 8;
    const tierSize = (maxCpc - minCpc) / tierCount;

    // Cas où tous les CPC sont identiques (tierSize = 0)
    if (tierSize === 0 || !isFinite(tierSize)) {
      // Mettre tous les termes dans une seule tranche
      let totalClicks = 0, totalCost = 0, totalConversions = 0, totalConversionsValue = 0;
      for (const term of searchTerms) {
        const clicks = Number(term.clicks) || 0;
        if (clicks > 0) {
          totalClicks += clicks;
          totalCost += Number(term.cost) || 0;
          totalConversions += Number(term.conversions) || 0;
          totalConversionsValue += Number(term.conversionsValue) || 0;
        }
      }
      const avgCos = totalConversionsValue > 0 ? (totalCost / totalConversionsValue * 100) : 0;
      return {
        tiers: [{
          tier: 1,
          cpcMin: Number(minCpc.toFixed(2)),
          cpcMax: Number(maxCpc.toFixed(2)),
          termCount: termsWithCpcCount,
          totalClicks,
          totalCost: Number(totalCost.toFixed(2)),
          totalConversions: Number(totalConversions.toFixed(1)),
          totalConversionsValue: Number(totalConversionsValue.toFixed(2)),
          avgCos: Number(avgCos.toFixed(1)),
          conversionRate: totalClicks > 0 ? Number((totalConversions / totalClicks * 100).toFixed(2)) : 0,
          avgCpa: totalConversions > 0 ? Number((totalCost / totalConversions).toFixed(2)) : 0
        }],
        totalTerms: searchTerms.length,
        termsWithCpc: termsWithCpcCount,
        cpcRange: { min: Number(minCpc.toFixed(2)), max: Number(maxCpc.toFixed(2)) }
      };
    }

    const tiers = new Array(tierCount);
    
    // Initialiser les tranches
    for (let i = 0; i < tierCount; i++) {
      tiers[i] = {
        tier: i + 1,
        cpcMin: Number((minCpc + (i * tierSize)).toFixed(2)),
        cpcMax: Number((i === tierCount - 1 ? maxCpc : minCpc + ((i + 1) * tierSize)).toFixed(2)),
        termCount: 0,
        totalClicks: 0,
        totalCost: 0,
        totalConversions: 0,
        totalConversionsValue: 0
      };
    }
    
    // Deuxième passe: agrégation par chunks
    
    // Deuxième passe: agrégation par chunks pour éviter les problèmes de mémoire
    for (let chunkStart = 0; chunkStart < searchTerms.length; chunkStart += CHUNK_SIZE) {
      const chunkEnd = Math.min(chunkStart + CHUNK_SIZE, searchTerms.length);
      const chunk = searchTerms.slice(chunkStart, chunkEnd);
      
      for (let i = 0; i < chunk.length; i++) {
        const term = chunk[i];
        const clicks = Number(term.clicks) || 0;
        const cost = Number(term.cost) || 0;
        const cpc = clicks > 0 ? (cost / clicks) : 0;
        
        if (cpc > 0) {
          // Trouver la tranche appropriée de manière optimisée
          let tierIndex = Math.floor((cpc - minCpc) / tierSize);
          if (tierIndex >= tierCount) tierIndex = tierCount - 1;
          if (tierIndex < 0) tierIndex = 0;
          
          const tier = tiers[tierIndex];
          tier.termCount++;
          tier.totalClicks += clicks;
          tier.totalCost += cost;
          tier.totalConversions += Number(term.conversions) || 0;
          tier.totalConversionsValue += Number(term.conversionsValue) || 0;
        }
      }
      
    }
    
    // Finaliser les calculs et filtrer les tranches vides
    const finalTiers = [];
    for (let i = 0; i < tierCount; i++) {
      const tier = tiers[i];
      
      if (tier.termCount > 0 || tier.totalClicks > 0) {
        const avgCos = tier.totalConversionsValue > 0 ? (tier.totalCost / tier.totalConversionsValue * 100) : 0;
        
        finalTiers.push({
          tier: tier.tier,
          cpcMin: tier.cpcMin,
          cpcMax: tier.cpcMax,
          termCount: tier.termCount,
          totalClicks: tier.totalClicks,
          totalCost: Number(tier.totalCost.toFixed(2)),
          totalConversions: Number(tier.totalConversions.toFixed(1)),
          totalConversionsValue: Number(tier.totalConversionsValue.toFixed(2)),
          avgCos: Number(avgCos.toFixed(1)),
          conversionRate: tier.totalClicks > 0 ? Number((tier.totalConversions / tier.totalClicks * 100).toFixed(2)) : 0,
          avgCpa: tier.totalConversions > 0 ? Number((tier.totalCost / tier.totalConversions).toFixed(2)) : 0
        });
        
      }
    }
    
    
    return {
      tiers: finalTiers,
      totalTerms: searchTerms.length,
      termsWithCpc: termsWithCpcCount,
      cpcRange: {
        min: Number(minCpc.toFixed(2)),
        max: Number(maxCpc.toFixed(2))
      }
    };
  };

  const loadSearchTermsCpcTiers = () => {
    if (!selectedAccount || !autoCharts) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Récupérer les données des termes de recherche depuis autoCharts
      const searchTermsBackendData = autoCharts.rawData && selectedAccount ? 
        autoCharts.rawData[selectedAccount.name] : null;
      
      
      let allSearchTerms = [];
      
      // Récupérer les termes de recherche Search/Shopping (tester plusieurs formats)
      let searchShoppingTerms = [];
      if (searchTermsBackendData) {
        // Format searchTermsData.terms
        if (searchTermsBackendData.searchTermsData && searchTermsBackendData.searchTermsData.terms) {
          searchShoppingTerms = searchTermsBackendData.searchTermsData.terms;
        }
        // Format alternatif searchTerms.searchTerms
        else if (searchTermsBackendData.searchTerms && searchTermsBackendData.searchTerms.searchTerms) {
          searchShoppingTerms = searchTermsBackendData.searchTerms.searchTerms;
        }
        // Format direct searchTerms
        else if (searchTermsBackendData.searchTerms && Array.isArray(searchTermsBackendData.searchTerms)) {
          searchShoppingTerms = searchTermsBackendData.searchTerms;
        }
        
        if (searchShoppingTerms.length > 0) {
          allSearchTerms = allSearchTerms.concat(searchShoppingTerms);
        }
      }
      
      // Récupérer les termes de recherche Performance Max
      
      let performanceMaxTerms = [];
      if (performanceMaxData && selectedAccount) {
        // Essayer selectedAccount.id puis selectedAccount.name (comme CombinedSearchTermsTable)
        const performanceMaxAccountData = performanceMaxData[selectedAccount.id] || performanceMaxData[selectedAccount.name];
        
        if (performanceMaxAccountData && performanceMaxAccountData.searchTerms && Array.isArray(performanceMaxAccountData.searchTerms)) {
            
            // Mapper les termes Performance Max avec calcul du CPC
            performanceMaxTerms = performanceMaxAccountData.searchTerms.map(term => {
              const clicks = Number(term.clicks) || 0;
              const cost = Number(term.cost) || 0;
              const cpc = clicks > 0 ? (cost / clicks) : 0;
              
              return {
                searchTerm: term.searchTerm || term.term || 'Non spécifié',
                campaignId: term.campaignId || null,
                campaignName: term.campaignName || 'N/A',
                impressions: term.impressions || 0,
                clicks: clicks,
                cost: cost,
                conversions: term.conversions || 0,
                conversionsValue: term.conversionsValue || 0,
                cpc: cpc,
                source: 'Performance Max'
              };
            });
            
          allSearchTerms = allSearchTerms.concat(performanceMaxTerms);
        }
      }
      
      // Filtrer par campagne si nécessaire
      if (campaignFilter && campaignFilter !== 'all') {
        allSearchTerms = allSearchTerms.filter(term => 
          term.campaignId === campaignFilter || term.campaignName === campaignFilter
        );
      }
      
      
      if (allSearchTerms.length > 0) {
        // Calculer les paliers côté client
        const tiersData = calculateSearchTermsCpcTiers(allSearchTerms);
        setTierData(tiersData);
      } else {
        setTierData(null);
      }
    } catch (err) {
      console.error('Erreur lors du calcul des paliers CPC:', err);
      setError('Erreur lors du calcul des paliers CPC');
      setTierData(null);
    } finally {
      setLoading(false);
    }
  };

  // Fonction de tri
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  // Trier et filtrer les données
  const sortedTiers = useMemo(() => {
    if (!tierData?.tiers) return [];
    
    let filteredTiers = [...tierData.tiers];
    
    // Si un filtre CPC est actif, afficher seulement la tranche correspondante
    if (selectedCpcTierFilter && selectedCpcTierFilter.min !== undefined && selectedCpcTierFilter.max !== undefined) {
      filteredTiers = tierData.tiers.filter(tier => 
        tier.cpcMin === selectedCpcTierFilter.min && tier.cpcMax === selectedCpcTierFilter.max
      );
    }
    
    return filteredTiers.sort((a, b) => {
      const aValue = a[sortConfig.key] ?? 0;
      const bValue = b[sortConfig.key] ?? 0;
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tierData, sortConfig, selectedCpcTierFilter]);

  useEffect(() => {
    if (selectedAccount && isVisible) {
      loadSearchTermsCpcTiers();
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, autoCharts, performanceMaxData, campaignFilter, isVisible]);

  // Formatter pour les tooltips du graphique
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <p className="label" style={{ fontWeight: 'bold', marginBottom: '5px' }}>
            Tranche {data.tier}: {formatNumber(data.cpcMin, 2)}€ - {formatNumber(data.cpcMax, 2)}€
          </p>
          <p style={{ color: '#82ca9d', margin: '2px 0' }}>
            Coût: €{formatNumber(data.totalCost, 2)}
          </p>
          <p style={{ color: '#8884d8', margin: '2px 0' }}>
            Conversions: {formatNumber(data.totalConversions, 1)}
          </p>
          <p style={{ color: '#ffc658', margin: '2px 0' }}>
            COS: {formatNumber(data.avgCos, 1)}%
          </p>
          <p style={{ color: '#666', margin: '2px 0' }}>
            Termes: {data.termCount}
          </p>
        </div>
      );
    }
    return null;
  };


  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><BarChart2 size={16} />&nbsp;Distribution des paliers CPC - Termes de recherche</h4>
        <div className="section-header-buttons">
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          <button
            onClick={onToggleVisibility}
            className="section-toggle-btn"
            title="Masquer/Afficher la section"
          >
            {isVisible ? '−' : '+'}
          </button>
        </div>
      </div>

      {!isVisible ? null : (
        <>
          {loading ? (
            <div className="loading-state" style={{ flexDirection: 'column' }}>
              <div className="loading-spinner"></div>
              Calcul des paliers CPC des termes de recherche...
            </div>
          ) : error ? (
            <div className="error-message">
              {error}
            </div>
          ) : !tierData || !tierData.tiers || tierData.tiers.length === 0 ? (
            <div className="empty-message">
              Aucune donnée CPC disponible pour les termes de recherche
            </div>
          ) : (
            <>
              {/* Résumé */}
              <div style={{ 
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '20px',
                display: 'flex',
                gap: '20px',
                flexWrap: 'wrap'
              }}>
                <div>
                  <strong>Termes analysés:</strong> {formatNumber(tierData.totalTerms)}
                </div>
                <div>
                  <strong>Plage de CPC:</strong> {formatNumber(tierData.cpcRange.min, 2)}€ - {formatNumber(tierData.cpcRange.max, 2)}€
                </div>
              </div>

              {/* Graphique en barres */}
              <div style={{ width: '100%', height: '400px', marginBottom: '20px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={sortedTiers.sort((a, b) => a.tier - b.tier).map(tier => ({
                      ...tier,
                      tierLabel: `${formatNumber(tier.cpcMin, 2)}€ - ${formatNumber(tier.cpcMax, 2)}€`
                    }))}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="tierLabel" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      fontSize={10}
                    />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar 
                      dataKey="totalCost" 
                      fill="#82ca9d" 
                      name="Coût (€)"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Tableau détaillé */}
              <div className="campaigns-table-wrapper">
                <table className="campaigns-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('cpcMin')} className="sortable">
                        Tranches de CPC {sortConfig.key === 'cpcMin' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th onClick={() => handleSort('termCount')} className="sortable numeric">
                        Termes {sortConfig.key === 'termCount' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      {isComponentMetricSelected('clics') && (
                        <th onClick={() => handleSort('totalClicks')} className="sortable numeric">
                          Clics {sortConfig.key === 'totalClicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cout') && (
                        <th onClick={() => handleSort('totalCost')} className="sortable numeric">
                          Coût {sortConfig.key === 'totalCost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('taux_conv') && (
                        <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                          Taux conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('conv') && (
                        <th onClick={() => handleSort('totalConversions')} className="sortable numeric">
                          Conv. {sortConfig.key === 'totalConversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cpa') && (
                        <th onClick={() => handleSort('avgCpa')} className="sortable numeric">
                          CPA {sortConfig.key === 'avgCpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('valeur_conv') && (
                        <th onClick={() => handleSort('totalConversionsValue')} className="sortable numeric">
                          Valeur conv. {sortConfig.key === 'totalConversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cos') && (
                        <th onClick={() => handleSort('avgCos')} className="sortable numeric">
                          COS {sortConfig.key === 'avgCos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTiers.map((tier) => {
                      const cosClass = getCosClassName(tier.avgCos, selectedAccount?.id || 'global', 'search_terms_cpc_tiers');
                      
                      return (
                        <tr key={tier.tier}>
                          <td 
                            onClick={() => onTierClick && onTierClick(tier.cpcMin, tier.cpcMax)}
                            style={{ 
                              cursor: onTierClick ? 'pointer' : 'default',
                              color: onTierClick ? '#333333' : 'inherit',
                              fontWeight: onTierClick ? '500' : 'normal'
                            }}
                            title={onTierClick ? 'Cliquer pour filtrer les termes de recherche par cette tranche de CPC' : ''}
                          >
                            {formatNumber(tier.cpcMin, 2)}€ - {formatNumber(tier.cpcMax, 2)}€
                          </td>
                          <td className="numeric">{formatNumber(tier.termCount)}</td>
                          {isComponentMetricSelected('clics') && (
                            <td className="numeric">{formatNumber(tier.totalClicks)}</td>
                          )}
                          {isComponentMetricSelected('cout') && (
                            <td className="numeric">€{formatNumber(tier.totalCost, 2)}</td>
                          )}
                          {isComponentMetricSelected('taux_conv') && (
                            <td className="numeric">{formatNumber(tier.conversionRate, 2)}%</td>
                          )}
                          {isComponentMetricSelected('conv') && (
                            <td className="numeric">{formatNumber(tier.totalConversions, 1)}</td>
                          )}
                          {isComponentMetricSelected('cpa') && (
                            <td className={`numeric ${getCpaClassName(tier.avgCpa, selectedAccount?.id || 'global', 'search_terms_cpc_tiers')}`}>
                              {tier.avgCpa >= 0 ? `€${formatNumber(tier.avgCpa, 2)}` : '-'}
                            </td>
                          )}
                          {isComponentMetricSelected('valeur_conv') && (
                            <td className="numeric">€{formatNumber(tier.totalConversionsValue, 2)}</td>
                          )}
                          {isComponentMetricSelected('cos') && (
                            <td className={`numeric ${cosClass}`}>
                              {formatNumber(tier.avgCos, 1)}%
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td><strong>TOTAL</strong></td>
                      <td className="numeric">
                        <strong>{formatNumber(sortedTiers.reduce((sum, tier) => sum + tier.termCount, 0))}</strong>
                      </td>
                      {isComponentMetricSelected('clics') && (
                        <td className="numeric">
                          <strong>{formatNumber(sortedTiers.reduce((sum, tier) => sum + tier.totalClicks, 0))}</strong>
                        </td>
                      )}
                      {isComponentMetricSelected('cout') && (
                        <td className="numeric">
                          <strong>€{formatNumber(sortedTiers.reduce((sum, tier) => sum + tier.totalCost, 0), 2)}</strong>
                        </td>
                      )}
                      {isComponentMetricSelected('taux_conv') && (
                        <td className="numeric">
                          <strong>
                            {formatNumber(
                              sortedTiers.reduce((sum, tier) => sum + tier.totalClicks, 0) > 0 
                                ? sortedTiers.reduce((sum, tier) => sum + tier.totalConversions, 0) / sortedTiers.reduce((sum, tier) => sum + tier.totalClicks, 0) * 100
                                : 0,
                              2
                            )}%
                          </strong>
                        </td>
                      )}
                      {isComponentMetricSelected('conv') && (
                        <td className="numeric">
                          <strong>{formatNumber(sortedTiers.reduce((sum, tier) => sum + tier.totalConversions, 0), 1)}</strong>
                        </td>
                      )}
                      {isComponentMetricSelected('cpa') && (
                        <td className={`numeric ${getCpaClassName(sortedTiers.reduce((sum, tier) => sum + tier.totalConversions, 0) > 0 ? sortedTiers.reduce((sum, tier) => sum + tier.totalCost, 0) / sortedTiers.reduce((sum, tier) => sum + tier.totalConversions, 0) : 0, selectedAccount?.id || 'global', 'search_terms_cpc_tiers')}`}>
                          <strong>
                            {sortedTiers.reduce((sum, tier) => sum + tier.totalConversions, 0) > 0 
                              ? `€${formatNumber(sortedTiers.reduce((sum, tier) => sum + tier.totalCost, 0) / sortedTiers.reduce((sum, tier) => sum + tier.totalConversions, 0), 2)}`
                              : '-'
                            }
                          </strong>
                        </td>
                      )}
                      {isComponentMetricSelected('valeur_conv') && (
                        <td className="numeric">
                          <strong>€{formatNumber(sortedTiers.reduce((sum, tier) => sum + tier.totalConversionsValue, 0), 2)}</strong>
                        </td>
                      )}
                      {isComponentMetricSelected('cos') && (
                        <td className={`numeric ${(() => {
                          const totalCos = sortedTiers.reduce((sum, tier) => sum + tier.totalConversionsValue, 0) > 0
                            ? sortedTiers.reduce((sum, tier) => sum + tier.totalCost, 0) / sortedTiers.reduce((sum, tier) => sum + tier.totalConversionsValue, 0) * 100
                            : 0;
                          return getCosClassName(totalCos, selectedAccount?.id || 'global', 'search_terms_cpc_tiers');
                        })()}`}>
                          <strong>
                            {formatNumber(
                              sortedTiers.reduce((sum, tier) => sum + tier.totalConversionsValue, 0) > 0 
                                ? sortedTiers.reduce((sum, tier) => sum + tier.totalCost, 0) / sortedTiers.reduce((sum, tier) => sum + tier.totalConversionsValue, 0) * 100
                                : 0,
                              1
                            )}%
                          </strong>
                        </td>
                      )}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </>
      )}

      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="search_terms_cpc_tiers"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Distribution CPC - Termes de recherche"
      />
    </div>
  );
};

export default SearchTermsCpcTierBreakdown;